import { useState } from 'react';

import unlinkInventory from '@/shared/api/product/unlinkInventory';
import updateProduct from '@/shared/api/product/updateProduct';
import updateProductVariant from '@/shared/api/product/updateProductVariant';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Product from '@/shared/types/products/product.type';
import ProductVariant from '@/shared/types/products/productVariant.type';
import Response from '@/shared/types/response';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type Args = {
  product: Product | null;
  onUpdateDialogState: OnUpdateDialogState;
};

const useInventoryTableActions = ({ product, onUpdateDialogState }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleMapInventoryToProduct = async (
    variant: Partial<ProductVariant>,
  ) => {
    onUpdateDialogState(DialogName.MapInventoryToProduct, { loading: true });

    if (variant.variant_name === '_default') {
      const response = await updateProduct(
        variant?.product_id || '',
        {
          is_sell_out_of_stock: variant.is_sell_out_of_stock,
          inventory_id: variant.inventory_id,
        },
        redirectLogin,
      );

      if (response.success) {
        onUpdateDialogState(DialogName.MapInventoryToProduct, {
          open: false,
          loading: false,
        });
        setRefreshTableIndex((prev) => prev + 1);
      } else {
        onUpdateDialogState(DialogName.MapInventoryToProduct, {
          loading: false,
          errors: [response.msg],
        });
      }
    } else {
      const response = await updateProductVariant({
        productId: variant?.product_id || '',
        variantId: variant?.variant_id || '',
        body: {
          is_sell_out_of_stock: variant.is_sell_out_of_stock,
          inventory_id: variant.inventory_id,
        },
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        onUpdateDialogState(DialogName.MapInventoryToProduct, {
          open: false,
          loading: false,
        });
        setRefreshTableIndex((prev) => prev + 1);
      } else {
        onUpdateDialogState(DialogName.MapInventoryToProduct, {
          loading: false,
          errors: [response.msg],
        });
      }
    }
  };

  const onIsSellOutOfStockUpdate = async (value: {
    productId: string;
    variantId: string;
    is_sell_out_of_stock: boolean;
  }) => {
    let response: Response<unknown>;

    if (product?.default_variant_id === value.variantId) {
      response = await updateProduct(
        value.productId,
        { is_sell_out_of_stock: value.is_sell_out_of_stock },
        redirectLogin,
      );
    } else {
      response = await updateProductVariant({
        productId: value.productId,
        variantId: value.variantId,
        body: { is_sell_out_of_stock: value.is_sell_out_of_stock },
        onUnauthorized: redirectLogin,
      });
    }

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
    }
  };

  const handleUnMap = async (value: {
    productId: string;
    variantId: string;
  }) => {
    onUpdateDialogState(DialogName.UnMapInventoryToProduct, {
      loading: true,
    });

    const response = await unlinkInventory({
      productId: value.productId,
      body: {
        exclude_product: value.variantId !== product?.default_variant_id,
        variant_ids: [value.variantId],
      },
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.UnMapInventoryToProduct, {
        loading: false,
        open: false,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.UnMapInventoryToProduct, {
        loading: false,
        errors: [response.msg],
      });
    }
  };

  return {
    refreshTableIndex,
    handleMapInventoryToProduct,
    onIsSellOutOfStockUpdate,
    handleUnMap,
  };
};

export default useInventoryTableActions;
